import React, { useMemo, useCallback, useState } from 'react';
import clsx from 'clsx';

import { BaseActionButtonProps } from '../BaseActionButtonProps';
import { GtmEvent } from 'lib/utils/gtm-utils';

import commonStyles from '../ActionButtonCommon.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import useDictionary from 'lib/hooks/useDictionary';
import { useContentItem } from 'lib/hooks/useContentItem';
import { waitForAllImages, forceLazyLoadImages } from 'lib/utils/load-images';

interface PrintButtonProps extends BaseActionButtonProps {
  isSubAction?: boolean;
}

export function PrintButton({
  label,
  variant,
  isSubAction = false,
  toolbarIdentifier,
  eventPositionSuffix,
}: PrintButtonProps): JSX.Element {
  const { getDictionaryValue } = useDictionary();
  const contentItem = useContentItem();
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const contentId = contentItem?.ContentId ?? '';
  const contentTitle = contentItem?.Title ?? '';
  const contentType = contentItem?.ContentType ?? '';
  // Get position attribute for GTM tracking
  const gttmPositionValue = useMemo((): string => {
    return [toolbarIdentifier, eventPositionSuffix].filter(Boolean).join('.') || '';
  }, [toolbarIdentifier, eventPositionSuffix]);

  const isIosDevice = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const userAgent = navigator.userAgent || '';
    const isIphoneOrIpad = /iPad|iPhone|iPod/.test(userAgent);
    const isIpadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return isIphoneOrIpad || isIpadOs;
  }, []);

  const handleOnClick = useCallback(async () => {
    const gtmEvent: GtmEvent = {
      event: 'print',
      content_id: contentId,
      content_title: contentTitle,
      content_type: contentType,
      record_type: contentType,
      position: gttmPositionValue,
    };

    pushToDataLayer({ ...gtmEvent } as DataLayerEvent);

    // Start loading state
    setIsPrintLoading(true);

    try {
      // Force load any lazy-loaded images
      forceLazyLoadImages();

      // Wait for all images to finish loading before printing.
      await waitForAllImages();

      // iOS Safari requires print to run directly in the click gesture.
      if (isIosDevice) {
        window.print();
        return;
      }

      // Non-iOS browsers can use a short defer to help layout stabilize.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
        });
      });
    } finally {
      // Reset loading state after print dialog closes
      setIsPrintLoading(false);
    }
  }, [gttmPositionValue, contentId, contentTitle, contentType, isIosDevice]);

  return (
    <div
      className={clsx({
        [commonStyles['actionButtonWrapper--horizontal']]: variant === 'horizontal',
      })}
    >
      <CtaButton
        ctaStyle={variant === 'horizontal' && !isSubAction ? 'link-invert-underline' : 'none'}
        ctaVariant="brand"
        className={clsx(
          commonStyles.actionButtonBase,
          commonStyles[`actionButton--${variant}`],
          isSubAction && commonStyles['actionButtonSubAction']
        )}
        onClick={handleOnClick}
        disabled={isPrintLoading}
        ctaIconBefore="print"
        ctaIconSize="sm"
        ctaIconNoSpacer={variant === 'vertical'}
        ctaIconClassName={commonStyles.icon}
        text={label}
        aria-label={
          `${getDictionaryValue('Components.ActionToolBar.PrintScreenReaderText')}` || label
        }
      />
    </div>
  );
}
